import React, { useState, useEffect, Fragment } from 'react'
import { Disclosure, Transition } from '@headlessui/react'
import { useIntl } from 'react-intl'
import { ToastContainer, toast } from 'react-toastify'
import { useConfig } from 'msp-integrations'
import { ChevronUpIcon } from '@heroicons/react/solid'
import { default as CountDown } from '@atoms/CountDown/CountDown'
import Icons from '@atoms/Icons/Icons'
import RichTextComponent from '@cms/RichText/BaseRichTextComponent'
import CustomLinkComponent from '@atoms/CustomLink/CustomLinkComponent'
import {
  default as TopInfoBannerType,
  TopInfoBannerBackgroundColors,
  CouponProps
} from './TopInfoBanner.types'
import { getStyles } from './TopInfoBanner.styles'

const Coupon = ({ code, cssClass }: CouponProps) => {
  const { formatMessage } = useIntl()
  const copyCode = (e: any) => {
    e.stopPropagation()
    navigator.clipboard.writeText(code)

    toast(
      <div className='text-12'>
        <Icons iconName={'Check'} size='lg' />
        {formatMessage({
          defaultMessage: 'Copied to clipboard',
          id: 'msp.topbanner.info.code.copied'
        })}
      </div>,
      {
        position: 'bottom-center',
        autoClose: 700,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        closeButton: false,
        theme: 'dark'
      }
    )
  }
  return (
    <div>
      <span className={`${cssClass} table-cell sm:pl-4`}>
        {' '}
        {formatMessage({
          defaultMessage: 'Code:',
          id: 'msp.topbanner.info.code.label'
        })}
        <span className='ml-2 mr-2 underline'>{code}</span>
      </span>
      <div onClick={copyCode} className='table-cell pb-1'>
        <Icons iconName={'Copy'} size='base' />
      </div>
    </div>
  )
}

const TopInfoBanner = (props: TopInfoBannerType) => {
  const {
    headline,
    textContent,
    backgroundColor = 'black',
    customCssClass = '',
    countdownDate = null,
    displayInfoBannerByDefault = true,
    couponCode = null,
    link = null,
    textLink = null
  } = props.data || props

  const {
    theme: { font }
  } = useConfig()
  const S = getStyles(font, TopInfoBannerBackgroundColors[backgroundColor].icon)
  const color = TopInfoBannerBackgroundColors[backgroundColor].bg
  const [hasCoundownExpired, setHasCoundownExpired] = useState(false)
  const [showInfoBanner, setShowInfoBanner] = useState(
    displayInfoBannerByDefault
  )

  useEffect(() => {
    if (hasCoundownExpired) {
      setShowInfoBanner(false)
    }
  }, [hasCoundownExpired])

  useEffect(() => {
    if (countdownDate?.value && !hasCoundownExpired && !showInfoBanner) {
      const isTimeExpired = new Date() > new Date(countdownDate.value)
      setShowInfoBanner(isTimeExpired === false)
      setHasCoundownExpired(isTimeExpired)
    }
  }, [])

  const TopInfoBannerIcons = {
    openIcon: <ChevronUpIcon className='h-5 w-5 rotate-180 transform' />,
    closeIcon: <ChevronUpIcon className='h-5 w-5' />
  }

  const onCountdownComplete = () => {
    setHasCoundownExpired(true)
    setShowInfoBanner(false)
  }

  return (
    <>
      <Transition
        as={Fragment}
        show={showInfoBanner}
        enter='ease-in-out duration-500'
        enterFrom='-translate-y-full'
        enterTo='translate-y-0'
        leave='ease-in-out duration-500'
        leaveFrom='translate-y-0'
        leaveTo='-translate-y-full'
      >
        <div
          id='top-info-banner'
          className={`${S.Default} ${color} ${customCssClass}`}
        >
          <Disclosure>
            {({ open }) => (
              <>
                <Disclosure.Button className={S.InfoToggleButton}>
                  <div className={S.InfoToggleButtonContent}>
                    <span className={S.InfoToggleTitle}>{headline}</span>
                    {open
                      ? TopInfoBannerIcons.closeIcon
                      : TopInfoBannerIcons.openIcon}
                  </div>
                  {countdownDate?.value && (
                    <CountDown
                      onComplete={onCountdownComplete}
                      countdownDate={countdownDate.value}
                      customCssClass='sm:ml-6 md:ml-10'
                    />
                  )}

                  {couponCode && (
                    <Coupon code={couponCode} cssClass={S.InfoToggleTitle} />
                  )}
                </Disclosure.Button>
                <Transition
                  show={open}
                  enter='transition duration-100 ease-out'
                  enterFrom='transform scale-95 opacity-0'
                  enterTo='transform scale-100 opacity-100'
                  leave='transition duration-75 ease-out'
                  leaveFrom='transform scale-100 opacity-100'
                  leaveTo='transform scale-95 opacity-0'
                >
                  <Disclosure.Panel className={S.InfoToggleContent}>
                    <RichTextComponent
                      className='text-14'
                      data={{
                        richText: textContent,
                        textColor: 'white',
                        hasCustomStyle: true
                      }}
                    />
                    {link && textLink && (
                      <CustomLinkComponent
                        href={link}
                        colorScheme='secondary'
                        className='text-14 tracking-0 mt-6 normal-case text-white'
                      >
                        {textLink}
                      </CustomLinkComponent>
                    )}
                  </Disclosure.Panel>
                </Transition>
              </>
            )}
          </Disclosure>
        </div>
      </Transition>
      <ToastContainer
        hideProgressBar
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme='dark'
      />
    </>
  )
}

export default TopInfoBanner
